import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  TextField,
  Button,
  MenuItem,
  IconButton,
  Chip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import PageHeader from "../../components/common/PageHeader";
import AsyncAutocompleteWithQuickCreate from "../../components/common/AsyncAutocompleteWithQuickCreate";
import QuickCreateLocation from "../../components/quickCreate/QuickCreateLocation";
import QuickCreateClient from "../../components/quickCreate/QuickCreateClient";
import QuickCreateProvider from "../../components/quickCreate/QuickCreateProvider";
import type { Location, Client, Provider } from "../../features/masterdata/types";
import FormSection from "../../components/common/FormSection";
import LoadingScreen from "../../components/common/LoadingScreen";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";

import { useClientSearch, useProviderSearch, useLocationSearch } from "../../hooks/useMasterdataSearch";

import {
  useCreateSeriesMutation,
  useGetSeriesQuery,
  useUpdateSeriesMutation,
} from "../../features/transfers/seriesApi"; // <- από το seriesApi που σου έδωσα πριν

import type { TransferSeriesWrite } from "../../features/transfers/types";

type StopType = "pickup" | "stop" | "dropoff";
type Frequency = "daily" | "weekly";
type SeriesStatus = "active" | "paused";

type SeriesStopUI = {
  sequence: number;
  stop_type: StopType;
  location: string; // UUID
  offset_minutes: number;
  notes?: string;
};

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
const WEEKDAY_LABELS: Record<string, string> = {
  mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun",
};
type Weekday = typeof WEEKDAYS[number];
const weekdaysToMask = (days: Weekday[]) =>
  days.reduce((mask, d) => mask | (1 << WEEKDAYS.indexOf(d)), 0);
const maskToWeekdays = (mask: number) =>
  WEEKDAYS.filter((d) => ((mask >> WEEKDAYS.indexOf(d)) & 1) === 1);

export default function SeriesFormPage() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id && id !== "new");
  const { enqueueSnackbar } = useSnackbar();

  // ---------- form state (needed early for search hooks) ----------
  const [client, setClient] = useState<string>("");
  const [defaultProvider, setDefaultProvider] = useState<string>("");
  const [isClientOpen, setClientOpen] = useState(false);
  const [isProviderOpen, setProviderOpen] = useState(false);

  // ---------- master data: fetch when dropdown open, then search-driven (page_size 5) ----------
  const clientSearch = useClientSearch(client || null, { menuOpen: isClientOpen });
  const providerSearch = useProviderSearch(defaultProvider || null, { menuOpen: isProviderOpen });
  const [isLocationOpen, setLocationOpen] = useState(false);
  const [locationDropdownOpenSequence, setLocationDropdownOpenSequence] = useState<number | null>(null);
  const locationOpenCountRef = useRef(0);
  const [newlyCreatedLocation, setNewlyCreatedLocation] = useState<Location | null>(null);
  const [newlyCreatedClient, setNewlyCreatedClient] = useState<Client | null>(null);
  const [newlyCreatedProvider, setNewlyCreatedProvider] = useState<Provider | null>(null);
  const [openQuickCreateLocation, setOpenQuickCreateLocation] = useState(false);
  const [openQuickCreateClient, setOpenQuickCreateClient] = useState(false);
  const [openQuickCreateProvider, setOpenQuickCreateProvider] = useState(false);
  const [quickCreateLocationForSequence, setQuickCreateLocationForSequence] = useState<number | null>(null);
  const clientsOptions = useMemo(
    () => [...clientSearch.options, ...(newlyCreatedClient ? [newlyCreatedClient] : [])],
    [clientSearch.options, newlyCreatedClient]
  );
  const providersOptions = useMemo(
    () => [...providerSearch.options, ...(newlyCreatedProvider ? [newlyCreatedProvider] : [])],
    [providerSearch.options, newlyCreatedProvider]
  );

  // ---------- load series on edit ----------
  const { data: series, isLoading: isLoadingSeries } = useGetSeriesQuery(id as string, {
    skip: !isEdit,
  });

  // ---------- rest of form state ----------
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState<string>("");
  const [startTime, setStartTime] = useState("");
  const [status, setStatus] = useState<SeriesStatus>("active");

  const [frequency, setFrequency] = useState<Frequency>("weekly");
  const [interval, setInterval] = useState<number>(1); // ✅ serializer expects interval
  const [weekdays, setWeekdays] = useState<Weekday[]>(["mon", "wed", "fri"]);

  const [priceToProvider, setPriceToProvider] = useState<string>("0");
  const [priceToClient, setPriceToClient] = useState<string>("0");
  const [passengersCount, setPassengersCount] = useState<string>("");

  const [stops, setStops] = useState<SeriesStopUI[]>([
    { sequence: 1, stop_type: "pickup", location: "", offset_minutes: 0 },
    { sequence: 2, stop_type: "dropoff", location: "", offset_minutes: 30 },
  ]);

  const selectedIdForLocationSearch =
    locationDropdownOpenSequence != null
      ? (stops.find((s) => s.sequence === locationDropdownOpenSequence)?.location ?? null)
      : null;
  const locationSearch = useLocationSearch(selectedIdForLocationSearch || null, {
    menuOpen: isLocationOpen,
  });
  const handleLocationMenuOpen = (sequence: number) => {
    locationOpenCountRef.current += 1;
    setLocationDropdownOpenSequence(sequence);
    setLocationOpen(true);
  };
  const handleLocationMenuClose = () => {
    locationOpenCountRef.current = Math.max(0, locationOpenCountRef.current - 1);
    if (locationOpenCountRef.current === 0) {
      setLocationOpen(false);
      setLocationDropdownOpenSequence(null);
    }
  };
  const locationsOptions = useMemo(
    () => [...locationSearch.options, ...(newlyCreatedLocation ? [newlyCreatedLocation] : [])],
    [locationSearch.options, newlyCreatedLocation]
  );

  // ---------- hydrate state from server ----------
  useEffect(() => {
    if (!series) return;

    setTitle(series.title || "");
    setStartDate(series.start_date || "");
    setEndDate(series.end_date || "");
    setStartTime(series.start_time?.slice(0, 5) || ""); // "HH:mm"
    setStatus((series.status as SeriesStatus) || "active");

    setFrequency((series.frequency as Frequency) || "daily");
    setInterval(series.interval || 1);

    if (series.frequency === "weekly") {
      const mask = series.days_of_week_mask ?? 0;
      setWeekdays(maskToWeekdays(mask));
    } else {
      setWeekdays(["mon"]); // δεν παίζει ρόλο στο daily
    }

    setClient(series.client || "");
    setDefaultProvider(series.default_provider || "");

    setPriceToProvider(String(series.price_to_provider ?? "0"));
    setPriceToClient(String(series.price_to_client ?? "0"));
    setPassengersCount(series.passengers_count ? String(series.passengers_count) : "");

    setStops(
      (series.stops || []).map((s) => ({
        sequence: s.sequence,
        stop_type: s.stop_type as StopType,
        location: s.location_id,
        offset_minutes: s.offset_minutes ?? 0,
        notes: s.notes || "",
      }))
    );
  }, [series]);

  const canSave = useMemo(() => {
    if (!title || !startDate || !endDate) return false;
    if (stops.length < 2) return false;

    const hasPickup = stops.some((s) => s.stop_type === "pickup");
    const hasDrop = stops.some((s) => s.stop_type === "dropoff");
    if (!hasPickup || !hasDrop) return false;

    if (frequency === "weekly" && weekdays.length === 0) return false;
    return true;
  }, [title, startDate, endDate, stops, frequency, weekdays]);

  const toggleWeekday = (d: Weekday) => {
    setWeekdays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  };

  const normalizeStops = (list: SeriesStopUI[]) =>
    list
      .slice()
      .sort((a, b) => a.sequence - b.sequence)
      .map((s, i) => ({ ...s, sequence: i + 1 }));

  const addStop = () => {
    setStops((prev) =>
      normalizeStops([...prev, { sequence: prev.length + 1, stop_type: "stop", location: "", offset_minutes: 0 }])
    );
  };

  const removeStop = (seq: number) => {
    setStops((prev) => normalizeStops(prev.filter((s) => s.sequence !== seq)));
  };

  const moveStop = (seq: number, dir: "up" | "down") => {
    setStops((prev) => {
      const sorted = prev.slice().sort((a, b) => a.sequence - b.sequence);
      const idx = sorted.findIndex((s) => s.sequence === seq);
      const swapWith = dir === "up" ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= sorted.length) return prev;
      const a = sorted[idx];
      const b = sorted[swapWith];
      [a.sequence, b.sequence] = [b.sequence, a.sequence];
      return normalizeStops(sorted);
    });
  };

  const updateStop = (seq: number, patch: Partial<SeriesStopUI>) => {
    setStops((prev) => normalizeStops(prev.map((s) => (s.sequence === seq ? { ...s, ...patch } : s))));
  };

  // ---------- mutations ----------
  const [createSeries, { isLoading: isCreating }] = useCreateSeriesMutation();
  const [updateSeries, { isLoading: isUpdating }] = useUpdateSeriesMutation();

  const onSave = async () => {
    try {
      const payload: TransferSeriesWrite = {
        title,
        client: client || null,

        start_date: startDate,
        end_date: endDate,
        start_time: startTime || null,

        frequency,
        interval: interval || 1,
        days_of_week_mask: frequency === "weekly" ? weekdaysToMask(weekdays) : null,

        default_provider: defaultProvider || null,

        price_to_provider: Number(priceToProvider || 0),
        price_to_client: Number(priceToClient || 0),
        passengers_count: passengersCount ? Number(passengersCount) : null,

        status,

        stops: stops.map((s) => ({
          sequence: s.sequence,
          stop_type: s.stop_type,
          location: s.location || null,
          offset_minutes: Number(s.offset_minutes || 0),
          notes: s.notes || "",
        })),
      };

      if (isEdit) {
        await updateSeries({ id: id as string, patch: payload }).unwrap();
        enqueueSnackbar("Series updated", { variant: "success" });
      } else {
        await createSeries(payload).unwrap();
        enqueueSnackbar("Series created", { variant: "success" });
      }

      nav("/app/series");
    } catch (e: any) {
      enqueueSnackbar(e?.data?.detail || "Failed to save series", { variant: "error" });
    }
  };

  const saving = isCreating || isUpdating;

  return (
    <Box>
      <PageHeader
        title={isEdit ? "Edit Series" : "New Series"}
        subtitle="Define a recurring schedule and generate transfers"
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => nav("/app/series")} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" disabled={!canSave || saving} onClick={onSave}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </Stack>
        }
      />

      {isEdit && isLoadingSeries ? (
        <LoadingScreen label="Loading series…" />
      ) : (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Stack spacing={3}>
            <FormSection title="Basic info" helperText="Title and status of the series.">
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Monday shuttle"
                  required
                  fullWidth
                />
                <TextField
                  label="Status"
                  select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as SeriesStatus)}
                  fullWidth
                  sx={{ minWidth: 0 }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                </TextField>
              </Stack>
            </FormSection>

            <FormSection title="Schedule" helperText="Transfers will be generated within this date range and time.">
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Start date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
                <TextField
                  label="End date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
                <TextField
                  label="Start time (optional)"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
            </FormSection>

            <FormSection
              title="Recurrence"
              helperText="When should transfers be generated? Weekly lets you pick specific days; daily runs every N days."
            >
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 80 }}>
                      Repeat
                    </Typography>
                    <TextField
                      label="Frequency"
                      select
                      size="small"
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value as any)}
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                    </TextField>
                    <TextField
                      label={frequency === "weekly" ? "Every N weeks" : "Every N days"}
                      type="number"
                      size="small"
                      value={interval}
                      onChange={(e) => setInterval(Number(e.target.value || 1))}
                      sx={{ width: 120 }}
                      inputProps={{ min: 1 }}
                    />
                  </Stack>

                  {frequency === "weekly" ? (
                    <>
                      <Typography variant="subtitle2" color="text.secondary">
                        On days
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {WEEKDAYS.map((d) => (
                          <Chip
                            key={d}
                            label={WEEKDAY_LABELS[d] ?? d}
                            onClick={() => toggleWeekday(d)}
                            color={weekdays.includes(d) ? "primary" : "default"}
                            variant={weekdays.includes(d) ? "filled" : "outlined"}
                            sx={{ textTransform: "none" }}
                          />
                        ))}
                      </Stack>
                      {weekdays.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Runs on: {weekdays.map((d) => WEEKDAY_LABELS[d] ?? d).join(", ")}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Runs every {interval} day{interval !== 1 ? "s" : ""}.
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </FormSection>

            <FormSection title="Defaults (optional)" helperText="Default client and provider for generated transfers. Use the dropdown to search or create new.">
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <AsyncAutocompleteWithQuickCreate<Client>
                    label="Client"
                    value={client}
                    onChange={(id) => setClient(id ?? "")}
                    options={clientsOptions}
                    getOptionLabel={(c) => c.name}
                    showCreate
                    createLabel="Create new client"
                    onOpenQuickCreate={() => setOpenQuickCreateClient(true)}
                    loading={clientSearch.loading}
                    onInputChange={(_, v) => clientSearch.setSearchTerm(v)}
                    filterOptions={(opts) => opts}
                    onOpen={() => setClientOpen(true)}
                    onClose={() => setClientOpen(false)}
                    placeholder="Type to search…"
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <AsyncAutocompleteWithQuickCreate<Provider>
                    label="Default provider"
                    value={defaultProvider}
                    onChange={(id) => setDefaultProvider(id ?? "")}
                    options={providersOptions}
                    getOptionLabel={(p) => p.name}
                    showCreate
                    createLabel="Create new provider"
                    onOpenQuickCreate={() => setOpenQuickCreateProvider(true)}
                    loading={providerSearch.loading}
                    onInputChange={(_, v) => providerSearch.setSearchTerm(v)}
                    filterOptions={(opts) => opts}
                    onOpen={() => setProviderOpen(true)}
                    onClose={() => setProviderOpen(false)}
                    placeholder="Type to search…"
                  />
                </Box>
              </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Cost (provider) — agreed price you pay per transfer"
                type="number"
                value={priceToProvider}
                onChange={(e) => setPriceToProvider(e.target.value)}
                placeholder="0"
                fullWidth
                inputProps={{ inputMode: "decimal", step: "0.01", min: 0 }}
              />
              <TextField
                label="Charge to client (optional) — what you charge the client"
                type="number"
                value={priceToClient}
                onChange={(e) => setPriceToClient(e.target.value)}
                placeholder="0"
                fullWidth
                inputProps={{ inputMode: "decimal", step: "0.01", min: 0 }}
              />
              <TextField
                label="Passengers (optional)"
                type="number"
                value={passengersCount}
                onChange={(e) => setPassengersCount(e.target.value)}
                placeholder="—"
                fullWidth
                inputProps={{ min: 0 }}
              />
            </Stack>
            </FormSection>

            <FormSection title="Stops template" helperText="Route used for every generated transfer. At least one pickup and one dropoff. Location is optional. Use arrows to reorder.">
              <Stack direction="row" justifyContent="flex-end" alignItems="center">
                <Button variant="outlined" onClick={addStop} startIcon={<ArrowDownwardOutlinedIcon sx={{ transform: "rotate(-90deg)" }} />}>
                  Add stop
                </Button>
              </Stack>

              <Stack spacing={1.5}>
                {stops.map((s, idx) => (
                  <Paper
                    key={s.sequence}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderLeft: "3px solid",
                      borderLeftColor:
                        s.stop_type === "pickup" ? "success.main" : s.stop_type === "dropoff" ? "warning.main" : "grey.400",
                    }}
                  >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 120 }}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            bgcolor: "action.selected",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            typography: "caption",
                            fontWeight: 700,
                          }}
                        >
                          {idx + 1}
                        </Box>
                        <Chip
                          size="small"
                          label={s.stop_type === "pickup" ? "Pickup" : s.stop_type === "dropoff" ? "Dropoff" : "Stop"}
                          color={s.stop_type === "pickup" ? "success" : s.stop_type === "dropoff" ? "warning" : "default"}
                          variant="filled"
                          onClick={() =>
                            updateStop(s.sequence, {
                              stop_type: s.stop_type === "pickup" ? "stop" : s.stop_type === "stop" ? "dropoff" : "pickup",
                            })
                          }
                          sx={{ textTransform: "capitalize", cursor: "pointer", minWidth: 88 }}
                          aria-label={`Stop type: ${s.stop_type}. Click to change.`}
                        />
                      </Stack>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <AsyncAutocompleteWithQuickCreate<Location>
                          label="Location (optional)"
                          value={s.location}
                          onChange={(id) => updateStop(s.sequence, { location: id ?? "" })}
                          options={locationsOptions}
                          getOptionLabel={(l) => l.name}
                          showCreate
                          createLabel="Create new location"
                          onOpenQuickCreate={() => {
                            setQuickCreateLocationForSequence(s.sequence);
                            setOpenQuickCreateLocation(true);
                          }}
                          loading={locationSearch.loading}
                          onInputChange={(_, v) => locationSearch.setSearchTerm(v)}
                          filterOptions={(opts) => opts}
                          onOpen={() => handleLocationMenuOpen(s.sequence)}
                          onClose={handleLocationMenuClose}
                          placeholder="Type to search…"
                        />
                      </Box>

                      <TextField
                        label="Offset (min)"
                        type="number"
                        size="small"
                        value={s.offset_minutes}
                        onChange={(e) => updateStop(s.sequence, { offset_minutes: Number(e.target.value || 0) })}
                        sx={{ minWidth: 100 }}
                        inputProps={{ min: 0 }}
                      />

                      <Stack direction="row" spacing={0.5}>
                        <IconButton size="small" onClick={() => moveStop(s.sequence, "up")} disabled={idx === 0} aria-label="Move up">
                          <ArrowUpwardOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => moveStop(s.sequence, "down")} disabled={idx === stops.length - 1} aria-label="Move down">
                          <ArrowDownwardOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => removeStop(s.sequence)} disabled={stops.length <= 2} size="small" aria-label="Remove stop">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>

                    <TextField
                      sx={{ mt: 2 }}
                      label="Notes (optional)"
                      value={s.notes || ""}
                      onChange={(e) => updateStop(s.sequence, { notes: e.target.value })}
                      fullWidth
                      size="small"
                      multiline
                      minRows={1}
                      placeholder="Stop-specific notes…"
                    />
                  </Paper>
                ))}
              </Stack>
            </FormSection>
          </Stack>
        </Paper>
      )}

      <QuickCreateClient
        open={openQuickCreateClient}
        onClose={() => setOpenQuickCreateClient(false)}
        onSuccess={(c) => {
          setNewlyCreatedClient(c);
          setClient(c.id);
          setOpenQuickCreateClient(false);
        }}
      />
      <QuickCreateProvider
        open={openQuickCreateProvider}
        onClose={() => setOpenQuickCreateProvider(false)}
        onSuccess={(p) => {
          setNewlyCreatedProvider(p);
          setDefaultProvider(p.id);
          setOpenQuickCreateProvider(false);
        }}
      />
      <QuickCreateLocation
        open={openQuickCreateLocation}
        onClose={() => {
          setOpenQuickCreateLocation(false);
          setQuickCreateLocationForSequence(null);
        }}
        onSuccess={(loc) => {
          setNewlyCreatedLocation(loc);
          if (quickCreateLocationForSequence != null) {
            setStops((prev) =>
              prev.map((stop) =>
                stop.sequence === quickCreateLocationForSequence ? { ...stop, location: loc.id } : stop
              )
            );
          }
          setOpenQuickCreateLocation(false);
          setQuickCreateLocationForSequence(null);
        }}
      />
    </Box>
  );
}
